import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/app/AuthProvider'
import { api } from '@/api/client'
import type { CertificateStore, UtmCertificate } from '@/features/certificates/types'
import {
  formatDateCompact,
  getCertByType,
  getStatusInfo,
  getStoreStatus,
  parseRuCertDate,
} from '@/features/certificates/utils'
import type { ChzLogRow } from '@/features/chz-journal/types'
import {
  formatChzDate,
  getRowStatus,
  isStaleSync,
  needsAttention,
} from '@/features/chz-journal/utils'
import { PERMISSIONS } from '@/shared/permissions'
import {
  fetchStoreIncidentsSummary,
  todayMsk,
  type StoreIncidentSummary,
} from '@/features/pos-incidents/api'
import styles from './HomeServicesSummary.module.css'

interface Props {
  storeId: string
  refreshKey?: number
  /** Уже загруженные данные ЕГАИС УТМ текущего магазина */
  utmByNode?: Record<string, Record<string, unknown>>
  /** Уже загруженный статус ЧЗ текущего магазина */
  chzByNode?: Record<string, Record<string, unknown>>
  /** Пока HomePage грузит инфраструктуру */
  devicesLoading?: boolean
}

type Tone = 'ok' | 'warn' | 'danger' | 'muted'

interface ServiceCardModel {
  key: string
  title: string
  path: string
  status: string
  tone: Tone
  lines: string[]
  loading: boolean
  empty?: boolean
}

interface MiLog {
  store_id?: number | string
  store_name?: string
  disk_use_percent?: string | number
  file_count?: number | string
  has_old_files?: boolean
  created_at?: string
}

function miStatus(log: MiLog): { label: string; tone: Tone } {
  const disk =
    log.disk_use_percent && log.disk_use_percent !== 'N/A'
      ? parseInt(String(log.disk_use_percent), 10)
      : 0
  if (disk >= 90) return { label: 'Критично', tone: 'danger' }
  if (log.has_old_files) return { label: 'Предупреждение', tone: 'warn' }
  return { label: 'Норма', tone: 'ok' }
}

function formatDisk(v: string | number | undefined): string {
  if (v == null) return '—'
  const value = String(v).trim()
  if (!value || value === 'N/A') return '—'
  const normalized = value.replace(/%+$/, '').trim()
  const n = Number(normalized)
  if (Number.isNaN(n)) return `${normalized} %`
  return `${n} %`
}

function formatUpdated(v?: string): string {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function isExpiringSoon(expireDate: unknown): boolean {
  const d = parseRuCertDate(expireDate)
  if (!d) return false
  const limit = new Date()
  limit.setDate(limit.getDate() + 30)
  return d <= limit
}

function certFromUtmField(raw: unknown, fallbackType: 'RSA' | 'GOST'): UtmCertificate {
  if (!raw || typeof raw !== 'object') {
    return {
      certType: fallbackType,
      startDate: 'Неизвестно',
      expireDate: 'Неизвестно',
      isExpiringSoon: false,
    }
  }
  const c = raw as Record<string, unknown>
  const startDate = String(c.startDate ?? 'Неизвестно')
  const expireDate = String(c.expireDate ?? 'Неизвестно')
  return {
    certType: String(c.certType || fallbackType),
    startDate,
    expireDate,
    isExpiringSoon: isExpiringSoon(expireDate),
  }
}

function certificateStoreFromUtm(
  storeId: number,
  utmByNode: Record<string, Record<string, unknown>>,
): CertificateStore | null {
  const entries = Object.values(utmByNode)
  if (!entries.length) return null

  const utm =
    entries.find((e) => !e.error && (e.rsa != null || e.gost != null)) || entries[0] || null
  if (!utm) return null

  if (utm.error) {
    return {
      storeId,
      nodeId: utm.nodeId != null ? Number(utm.nodeId) : undefined,
      nodeName: utm.nodeName != null ? String(utm.nodeName) : undefined,
      certificates: [
        { certType: 'RSA', startDate: 'Ошибка', expireDate: 'Ошибка', isExpiringSoon: false },
        { certType: 'GOST', startDate: 'Ошибка', expireDate: 'Ошибка', isExpiringSoon: false },
      ],
    }
  }

  return {
    storeId,
    nodeId: utm.nodeId != null ? Number(utm.nodeId) : undefined,
    nodeName: utm.nodeName != null ? String(utm.nodeName) : undefined,
    certificates: [certFromUtmField(utm.rsa, 'RSA'), certFromUtmField(utm.gost, 'GOST')],
  }
}

function chzLogFromNodes(
  storeId: number,
  chzByNode: Record<string, Record<string, unknown>>,
): ChzLogRow | null {
  const entries = Object.values(chzByNode)
  if (!entries.length) return null
  const chz = entries.find((e) => !e.error) || entries[0]
  if (!chz) return null
  if (chz.error) {
    return {
      store_id: storeId,
      status: 'problem',
      version: undefined,
      last_sync: undefined,
    }
  }
  const lastSync = chz.lastSync ?? chz.last_sync
  return {
    store_id: storeId,
    status: chz.status != null ? String(chz.status) : undefined,
    version: chz.version != null ? String(chz.version) : undefined,
    last_sync: lastSync != null ? String(lastSync) : undefined,
  }
}

function buildMiCard(log: MiLog | null, loading: boolean): ServiceCardModel {
  if (loading) {
    return {
      key: 'mi',
      title: 'Журнал МИ',
      path: '/journals/integration',
      status: '…',
      tone: 'muted',
      lines: [],
      loading: true,
    }
  }
  if (!log) {
    return {
      key: 'mi',
      title: 'Журнал МИ',
      path: '/journals/integration',
      status: 'Нет данных',
      tone: 'muted',
      lines: ['Запись для магазина не найдена'],
      loading: false,
      empty: true,
    }
  }
  const st = miStatus(log)
  const lines = [
    `Диск ${formatDisk(log.disk_use_percent)}`,
    log.has_old_files
      ? `Старые файлы: ${log.file_count ?? 'да'}`
      : `Файлов: ${log.file_count ?? '—'}`,
    `Обновлено ${formatUpdated(log.created_at)}`,
  ]
  return {
    key: 'mi',
    title: 'Журнал МИ',
    path: '/journals/integration',
    status: st.label,
    tone: st.tone,
    lines,
    loading: false,
  }
}

function buildChzCard(log: ChzLogRow | null, loading: boolean): ServiceCardModel {
  if (loading) {
    return {
      key: 'chz',
      title: 'Журнал ЛМЧЗ',
      path: '/journals/chz',
      status: '…',
      tone: 'muted',
      lines: [],
      loading: true,
    }
  }
  if (!log) {
    return {
      key: 'chz',
      title: 'Журнал ЛМЧЗ',
      path: '/journals/chz',
      status: 'Нет данных',
      tone: 'muted',
      lines: ['Узел ЧЗ для магазина не найден'],
      loading: false,
      empty: true,
    }
  }
  const ready = getRowStatus(log) === 'ready'
  const attention = needsAttention(log)
  const stale = isStaleSync(log)
  let status = ready ? 'Готов' : 'Проблема'
  let tone: Tone = ready ? 'ok' : 'danger'
  if (ready && stale) {
    status = 'Синхр. устарела'
    tone = 'warn'
  } else if (ready && attention) {
    status = 'Внимание'
    tone = 'warn'
  }
  const lines = [
    log.version ? `Версия ${log.version}` : 'Версия —',
    `Синхр. ${formatChzDate(log.last_sync)}`,
  ]
  return {
    key: 'chz',
    title: 'Журнал ЛМЧЗ',
    path: '/journals/chz',
    status,
    tone,
    lines,
    loading: false,
  }
}

function buildCertCard(store: CertificateStore | null, loading: boolean): ServiceCardModel {
  if (loading) {
    return {
      key: 'certs',
      title: 'Сертификаты УТМ',
      path: '/certificates',
      status: '…',
      tone: 'muted',
      lines: [],
      loading: true,
    }
  }
  if (!store) {
    return {
      key: 'certs',
      title: 'Сертификаты УТМ',
      path: '/certificates',
      status: 'Нет данных',
      tone: 'muted',
      lines: ['УТМ для магазина не найден'],
      loading: false,
      empty: true,
    }
  }
  const statusKey = getStoreStatus(store)
  const info = getStatusInfo(statusKey)
  const tone: Tone =
    info.tone === 'bad' ? 'danger' : info.tone === 'warn' ? 'warn' : info.tone === 'muted' ? 'muted' : 'ok'
  const rsa = getCertByType(store, 'RSA')
  const gost = getCertByType(store, 'GOST')
  const lines = [
    `RSA ${formatDateCompact(rsa?.expireDate)}`,
    `GOST ${formatDateCompact(gost?.expireDate)}`,
  ]
  return {
    key: 'certs',
    title: 'Сертификаты УТМ',
    path: '/certificates',
    status: info.text,
    tone,
    lines,
    loading: false,
  }
}

function buildIncidentsCard(
  summary: StoreIncidentSummary | null,
  loading: boolean,
): ServiceCardModel {
  const path = summary?.new ? '/cash/incidents?status=new' : '/cash/incidents'
  if (loading) {
    return {
      key: 'pos-incidents',
      title: 'Инциденты касс',
      path,
      status: '…',
      tone: 'muted',
      lines: [],
      loading: true,
    }
  }
  if (!summary) {
    return {
      key: 'pos-incidents',
      title: 'Инциденты касс',
      path: '/cash/incidents',
      status: 'Нет данных',
      tone: 'muted',
      lines: ['ActivePOS за день'],
      loading: false,
      empty: true,
    }
  }
  const neu = summary.new || 0
  const total = summary.total || 0
  let status = 'Норма'
  let tone: Tone = 'ok'
  if (neu > 0) {
    status = `${neu} новых`
    tone = neu >= 5 ? 'danger' : 'warn'
  } else if (total > 0) {
    status = 'Просмотрено'
    tone = 'ok'
  } else {
    status = 'Чисто'
    tone = 'ok'
  }
  return {
    key: 'pos-incidents',
    title: 'Инциденты касс',
    path,
    status,
    tone,
    lines: [
      `${neu} новых · ${total} за день`,
      summary.by_terminal?.length
        ? `Касс с событиями: ${summary.by_terminal.length}`
        : 'Касс с событиями: 0',
    ],
    loading: false,
  }
}

export function HomeServicesSummary({
  storeId,
  refreshKey = 0,
  utmByNode = {},
  chzByNode = {},
  devicesLoading = false,
}: Props) {
  const { hasPermission } = useAuth()
  const navigate = useNavigate()

  const canMi = hasPermission(PERMISSIONS.integrationJournal)
  const canChz = hasPermission(PERMISSIONS.chzJournal)
  const canCerts = hasPermission(PERMISSIONS.certificates)
  const canIncidents = hasPermission(PERMISSIONS.trassir)

  const [mi, setMi] = useState<MiLog | null>(null)
  const [loadingMi, setLoadingMi] = useState(false)
  const [incidentsSummary, setIncidentsSummary] = useState<StoreIncidentSummary | null>(null)
  const [loadingIncidents, setLoadingIncidents] = useState(false)

  useEffect(() => {
    const id = Number(storeId)
    if (!Number.isFinite(id) || !canMi) {
      setMi(null)
      setLoadingMi(false)
      return
    }

    const ac = new AbortController()
    setLoadingMi(true)
    void api<MiLog[] | { logs?: MiLog[] }>(
      `/api/background/integration-logs?storeId=${encodeURIComponent(String(id))}`,
      { signal: ac.signal },
    )
      .then((res) => {
        if (ac.signal.aborted) return
        const rows = Array.isArray(res) ? res : res.logs || []
        setMi(rows[0] || null)
      })
      .catch(() => {
        if (!ac.signal.aborted) setMi(null)
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoadingMi(false)
      })

    return () => ac.abort()
  }, [storeId, refreshKey, canMi])

  useEffect(() => {
    const id = Number(storeId)
    if (!Number.isFinite(id) || !canIncidents) {
      setIncidentsSummary(null)
      setLoadingIncidents(false)
      return
    }
    const ac = new AbortController()
    setLoadingIncidents(true)
    void fetchStoreIncidentsSummary(id, { day: todayMsk() })
      .then((res) => {
        if (!ac.signal.aborted) setIncidentsSummary(res)
      })
      .catch(() => {
        if (!ac.signal.aborted) setIncidentsSummary(null)
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoadingIncidents(false)
      })
    return () => ac.abort()
  }, [storeId, refreshKey, canIncidents])

  const certStore = useMemo(() => {
    if (!canCerts) return null
    const id = Number(storeId)
    if (!Number.isFinite(id)) return null
    return certificateStoreFromUtm(id, utmByNode)
  }, [canCerts, storeId, utmByNode])

  const chzLog = useMemo(() => {
    if (!canChz) return null
    const id = Number(storeId)
    if (!Number.isFinite(id)) return null
    return chzLogFromNodes(id, chzByNode)
  }, [canChz, storeId, chzByNode])

  const certLoading = canCerts && devicesLoading && Object.keys(utmByNode).length === 0
  const chzLoading = canChz && devicesLoading && Object.keys(chzByNode).length === 0

  const cards: ServiceCardModel[] = []
  if (canMi) cards.push(buildMiCard(mi, loadingMi))
  if (canChz) cards.push(buildChzCard(chzLog, chzLoading))
  if (canCerts) cards.push(buildCertCard(certStore, certLoading))
  if (canIncidents) cards.push(buildIncidentsCard(incidentsSummary, loadingIncidents))

  if (!cards.length) return null

  return (
    <div className={styles.grid} style={{ gridTemplateColumns: `repeat(${cards.length}, 1fr)` }}>
      {cards.map((card) => (
        <button
          key={card.key}
          type="button"
          className={`${styles.card} ${card.loading ? styles.cardLoading : ''}`}
          onClick={() => navigate(card.path)}
        >
          <div className={styles.head}>
            <span className={styles.title}>{card.title}</span>
            <span className={`${styles.status} ${styles[`tone_${card.tone}`]}`}>
              <i className={`${styles.dot} ${styles[`dot_${card.tone}`]}`} />
              {card.status}
            </span>
          </div>
          {card.lines.length > 0 ? (
            <ul className={styles.lines}>
              {card.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : (
            <div className={styles.placeholder}>{card.loading ? 'Загрузка…' : null}</div>
          )}
        </button>
      ))}
    </div>
  )
}
